/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, { useCallback, useEffect, useState } from 'react';

import ExperimentalCalendar from '../ExperimentalCalendar';
import { CalendarIcon, ChevronDownIcon } from '../Icons';

import Styles from './index.module.scss';

import type { DateTime } from 'luxon';

import { useClickOutside } from '@/app/hooks/useClickOutside';
import { addDays, endOfDay, format, isBefore } from '@/app/utils/date';

interface Props extends React.ComponentPropsWithRef<typeof ExperimentalCalendar> {
	onChange: (value: { start: DateTime; end: DateTime }) => void;
}

const CalendarPicker = (props: Props) => {
	const [visible, setVisible] = useState(false);
	const [matches, setMatches] = useState(false);
	const [headerActiveButton, setHeaderActiveButton] = useState<'From' | 'To'>('From');
	const { ref } = useClickOutside<HTMLDivElement>(() => {
		setVisible(false);
	});

	useEffect(() => {
		visible && setHeaderActiveButton('From');
	}, [visible]);

	useEffect(() => {
		props.selectionMode === 'custom' && setVisible(true);
	}, [props.selectionMode]);

	useEffect(() => {
		const media = window.matchMedia('(min-width: 920px)');
		if (media.matches !== matches) {
			setMatches(media.matches);
		}
		const listener = () => {
			setMatches(media.matches);
		};

		media.addEventListener('change', listener);

		return () => media.removeEventListener('change', listener);
	}, [matches]);

	const { onChange, ...rest } = props;

	const handleChange = useCallback(
		(value: { start: DateTime; end: DateTime }) => {
			if (!onChange) {
				return;
			}

			if (props.selectionMode !== 'custom') {
				onChange(value);
				setVisible(false);
				return;
			}

			if (headerActiveButton === 'From') {
				setHeaderActiveButton('To');
				onChange({ start: value.start, end: endOfDay(addDays(value.start, 1)) });
				return;
			}

			const start = props.value?.start;
			if (!start) {
				return;
			}

			if (isBefore(value.end, value.start)) {
				onChange({
					start: value.end.plus({ days: -1 }).startOf('day'),
					end: value.end,
				});
			}

			onChange({ start: start, end: value.end });
		},
		[onChange, props.selectionMode, props.value?.start, headerActiveButton]
	);

	const handleOpen = useCallback(() => {
		setVisible(true);
		setHeaderActiveButton('From');
	}, []);

	const handleHeaderClick = (mode: 'From' | 'To') => () => {
		setHeaderActiveButton(mode);
	};

	const calendar = (
		<>
			{rest.selectionMode === 'custom' && (
				<div className={Styles.pickerHeader}>
					<button
						className={headerActiveButton === 'From' ? Styles.active : ''}
						onClick={handleHeaderClick('From')}
					>
						<div className={Styles.iconButton}>
							<ChevronDownIcon />
						</div>
						{props.value?.start && `${format(props.value.start, 'EEE, dd MMM yyyy')}`}
					</button>
					<button
						className={headerActiveButton === 'To' ? Styles.active : ''}
						onClick={handleHeaderClick('To')}
					>
						{props.value?.end && `${format(props.value.end, 'EEE, dd MMM yyyy')}`}
						<div className={Styles.iconButton}>
							<ChevronDownIcon />
						</div>
					</button>
				</div>
			)}
			<ExperimentalCalendar
				{...rest}
				onChange={handleChange}
				focusDate={
					props.selectionMode === 'custom'
						? headerActiveButton === 'From'
							? props.value?.start
							: props.value?.end
						: undefined
				}
			/>
		</>
	);

	return (
		<div className={Styles.container}>
			<div
				className={`${Styles.button} ${visible ? Styles.active : ''}`}
				onClick={handleOpen}
				title="Select Date"
			>
				<CalendarIcon />
			</div>

			{visible && (
				<div className={Styles.popup} ref={ref}>
					{calendar}
				</div>
			)}
		</div>
	);
};

export default CalendarPicker;
